import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Plus } from "lucide-react";
import AppHeader from "@/components/layout/AppHeader";
import { api } from "@/lib/api";

const FORMAS = [["m_s", "Hombre"], ["f_s", "Mujer"], ["m_p", "Varios / mixto"], ["f_p", "Varias mujeres"], ["moral", "Persona moral"]];
const vacio = { clave: "", rol: "", m_s: "", f_s: "", m_p: "", f_p: "", moral: "", por_omision: "m" };

function AlertasGeneracion() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/alertas-generacion").then((r) => setItems(r.data.filter((a) => !a.visto))).catch(() => {});
  }, []);
  if (!items.length) return null;
  const vistas = async () => { await api.post("/admin/alertas-generacion/vistas"); setItems([]); };
  return (
    <section className="rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="alertas-generacion">
      <div className="font-semibold text-red-800 text-sm">Documentos que no se generaron por un marcador sin reemplazar</div>
      <ul className="mt-2 text-xs text-red-700 space-y-1">
        {items.map((a) => <li key={a.id}>{new Date(a.fecha).toLocaleString("es-MX")} · {a.tipo} (versión {a.plantilla_version ?? "semilla"}) · marcador: <code>{a.marcador}</code></li>)}
      </ul>
      <button onClick={vistas} className="mt-3 text-xs font-semibold text-red-800 underline" data-testid="alertas-vistas">Marcar como revisadas</button>
    </section>
  );
}

function TerminoRow({ t, onSaved }) {
  const [f, setF] = useState(t);
  const save = async () => {
    try { await api.post("/admin/concordancia", f); toast.success(`Guardado ${f.clave}`); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); }
  };
  return (
    <div className="rounded-xl border border-[#E2DCD2] p-3 space-y-2" data-testid={`termino-${t.clave || "nuevo"}`}>
      <div className="grid grid-cols-2 gap-2">
        <input value={f.clave} onChange={(e) => setF({ ...f, clave: e.target.value })} placeholder="Clave (ej. DEPOSITARIO)" className="h-10 px-2 rounded-lg border text-sm" data-testid="termino-clave" />
        <input value={f.rol} onChange={(e) => setF({ ...f, rol: e.target.value })} placeholder="Rol (ej. depositario)" className="h-10 px-2 rounded-lg border text-sm" data-testid="termino-rol" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {FORMAS.map(([k, l]) => (
          <input key={k} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={l} title={l}
                 className="h-10 px-2 rounded-lg border text-xs" data-testid={`termino-${k}`} />
        ))}
      </div>
      <button onClick={save} className="text-sm font-semibold text-[#1E3A5F]" data-testid="termino-save">Guardar</button>
    </div>
  );
}

function FraseRow({ fr, onSaved }) {
  const [f, setF] = useState(fr);
  const save = async () => {
    try { await api.put(`/admin/frases/${fr.clave}`, { texto_m: f.texto_m, texto_f: f.texto_f }); toast.success("Frase guardada"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); }
  };
  return (
    <div className="rounded-xl border border-[#E2DCD2] p-3 space-y-2" data-testid={`frase-${fr.clave}`}>
      <div className="text-xs font-semibold text-slate-600">{fr.descripcion} · {fr.clave}</div>
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="text-[11px] font-semibold text-slate-500">Hombre
          <input value={f.texto_m} onChange={(e) => setF({ ...f, texto_m: e.target.value })} className="mt-0.5 w-full h-10 px-2 rounded-lg border text-sm font-normal text-slate-900" data-testid={`frase-m-${fr.clave}`} />
        </label>
        <label className="text-[11px] font-semibold text-slate-500">Mujer
          <input value={f.texto_f} onChange={(e) => setF({ ...f, texto_f: e.target.value })} className="mt-0.5 w-full h-10 px-2 rounded-lg border text-sm font-normal text-slate-900" data-testid={`frase-f-${fr.clave}`} />
        </label>
      </div>
      <button onClick={save} className="text-sm font-semibold text-[#1E3A5F]" data-testid={`frase-save-${fr.clave}`}>Guardar</button>
    </div>
  );
}

function NuevoTipo({ onSaved }) {
  const [f, setF] = useState({ clave: "", nombre: "", categoria: "venta" });
  const save = async () => {
    try { await api.post("/admin/tipos-contrato", { ...f, activo: false }); toast.success("Tipo creado (inactivo)"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo crear"); }
  };
  return (
    <div className="bm-card p-4 grid sm:grid-cols-4 gap-2" data-testid="nuevo-tipo-form">
      <input value={f.clave} onChange={(e) => setF({ ...f, clave: e.target.value })} placeholder="clave (ej. comodato_muebles)" className="h-10 px-2 rounded-lg border text-sm" data-testid="nuevo-tipo-clave" />
      <input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} placeholder="Nombre" className="h-10 px-2 rounded-lg border text-sm" data-testid="nuevo-tipo-nombre" />
      <select value={f.categoria} onChange={(e) => setF({ ...f, categoria: e.target.value })} className="h-10 px-2 rounded-lg border text-sm" data-testid="nuevo-tipo-categoria">
        <option value="venta">Venta</option><option value="renta">Renta</option><option value="otro">Otro</option>
      </select>
      <button onClick={save} className="bm-btn-primary" data-testid="nuevo-tipo-save"><Plus className="w-4 h-4" /> Agregar</button>
    </div>
  );
}

export default function AdminPlantillasPage() {
  const [tipos, setTipos] = useState([]);
  const [terminos, setTerminos] = useState([]);
  const [frases, setFrases] = useState([]);
  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([api.get("/admin/tipos-contrato"), api.get("/admin/concordancia"), api.get("/admin/frases")]);
    setTipos(a.data); setTerminos(b.data); setFrases(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (t) => { await api.put(`/admin/tipos-contrato/${t.clave}`, { ...t, activo: !t.activo }); load(); };

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="admin-plantillas-page">
        <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-slate-900">Plantillas de contratos</h1>
        <AlertasGeneracion />
        <section className="space-y-2">
          {tipos.map((t) => (
            <div key={t.clave} className="bm-card p-4 flex items-center justify-between gap-3" data-testid={`tipo-${t.clave}`}>
              <Link to={`/app/admin/plantillas/${t.clave}`} className="flex items-center gap-3 min-w-0" data-testid={`tipo-editar-${t.clave}`}>
                <FileText className="w-5 h-5 text-[#2A5C55] shrink-0" />
                <div className="min-w-0"><div className="font-semibold truncate">{t.nombre}</div><div className="text-xs text-slate-500">{t.categoria} · {t.clave}</div></div>
              </Link>
              <button onClick={() => toggle(t)} data-testid={`tipo-activo-${t.clave}`}
                      className={`text-xs font-bold px-3 py-1.5 rounded-full ${t.activo ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {t.activo ? "Activo" : "Inactivo"}
              </button>
            </div>
          ))}
          <NuevoTipo onSaved={load} />
        </section>
        <section className="space-y-2" data-testid="frases-section">
          <h2 className="font-heading font-bold text-lg">Estado civil, identificación y cónyuge</h2>
          <p className="text-sm text-slate-600">Las palabras entre llaves, como {"{numero}"}, se sustituyen por los datos reales al generar el contrato. No las borres ni las cambies; edita solo el texto que las rodea.</p>
          {frases.map((f) => <FraseRow key={f.clave} fr={f} onSaved={load} />)}
        </section>
        <section className="space-y-2" data-testid="concordancia-section">
          <h2 className="font-heading font-bold text-lg">Concordancia de género y número</h2>
          <p className="text-sm text-slate-600">Úsalo en la plantilla como {"{{EL_CLAVE}}"}. El rol liga el término con las personas del contrato.</p>
          {terminos.map((t) => <TerminoRow key={t.clave} t={t} onSaved={load} />)}
          <TerminoRow t={vacio} onSaved={load} />
        </section>
      </main>
    </div>
  );
}
